/**
 * ServiceStepsContainer -- data / logic layer for the service steps tab.
 *
 * Fetches step definitions from GET /api/v1/service-steps (backend source of
 * truth). Different services have different step counts (5, 6, or 8 steps).
 * Maps the current workflow state to the appropriate active step index using
 * a proportional slot formula so every service renders correctly regardless
 * of step count.
 */
import * as React from 'react';
import { Loader2 } from 'lucide-react';
import type { CustomerServiceRequestResponse } from '@/api/service-requests';
import { useServiceSteps, type BackendServiceStep } from '@/api/service-requests';
import type { ResolvedServiceStep, StepStatus } from '../constants';
import { ServiceStepsView } from './service-steps-view';

// ---------------------------------------------------------------------------
// Workflow-state -> active slot mapping
// ---------------------------------------------------------------------------
//
// The backend has 7 workflow states. We collapse them into 5 "active slots"
// (0-4) — slot 5 is the sentinel meaning "all steps complete".
//
// Slot formula: activeStepIndex = floor( slot * (N-1) / (SLOT_MAX) )
// where N = total steps for this service, SLOT_MAX = 4 (last active slot).
//
// This gives a proportional spread across any step count:
//   N=5  -> slots 0-4 map to indices 0,1,2,3,4
//   N=6  -> slots 0-4 map to indices 0,1,2,3,5  (step 4 skipped as current)
//   N=8  -> slots 0-4 map to indices 0,2,4,6,7  (evenly spread)

const STATE_SLOT: Record<string, number> = {
  service_selected:             0,
  docs_start:                   0, // still on first step (Document Upload/Onboarding)
  provider_assignment_start:    1,
  provider_assignment_complete: 2,
  platform_clarification:       2,
  draft_evaluation:             3,
  acknowledgement_submission:   4,
  acknowledgement_submitted:    4,
  service_request_complete:     5, // sentinel: all steps complete
  service_delivered:            5, // backend alias for service_request_complete
};

const SLOT_MAX = 4; // highest active slot index

function resolveSteps(
  backendSteps: BackendServiceStep[],
  workflowState: string,
): ResolvedServiceStep[] {
  const slot = STATE_SLOT[workflowState] ?? 0;
  const n    = backendSteps.length;
  const allComplete = slot > SLOT_MAX;

  // Proportional index: slot [0..SLOT_MAX] -> stepIndex [0..n-1]
  const activeIdx = allComplete
    ? n // beyond array -> mark everything completed
    : Math.min(n - 1, Math.floor((slot * (n - 1)) / SLOT_MAX));

  return backendSteps.map((s, idx) => {
    let status: StepStatus;
    if (allComplete || idx < activeIdx) {
      status = 'completed';
    } else if (idx === activeIdx) {
      status = 'current';
    } else {
      status = 'upcoming';
    }
    return { title: s.title, desc: s.description, status };
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ServiceStepsContainerProps {
  request: CustomerServiceRequestResponse;
}

export function ServiceStepsContainer({ request }: ServiceStepsContainerProps) {
  const serviceType   = request.service_type ?? '';
  const workflowState = request.customer_workflow_state ?? '';

  const { data: stepsData, isLoading, isError } = useServiceSteps();

  const steps = React.useMemo(() => {
    if (!stepsData) return [];
    // Use service-specific steps or fall back to 'default'
    const raw = stepsData.items[serviceType] ?? stepsData.items['default'] ?? [];
    return resolveSteps(raw, workflowState);
  }, [stepsData, serviceType, workflowState]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span className="text-sm">Loading service steps…</span>
      </div>
    );
  }

  if (isError || steps.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-6 text-center">
        Could not load service steps. Please refresh the page.
      </p>
    );
  }

  return <ServiceStepsView steps={steps} />;
}
